import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { baseUrl } from "../../api/config";
import {
    CalendarSlot,
    CalendarResponse,
    ReservedSlotData,
    UseReservedSlotsCalendarParams,
    AvailabilitySlot,
    SlotsByService,
    BookingSlot,
    BookingsByService,
    NfbBlockedRule,
} from "./types";
import { buildBookingsIndex, slotKey } from "./shared/bookingsIndex";
import {
    DAYS_WINDOW_CALENDAR_SPAN,
    PSY_HOURLY_SLOTS,
    NFB_WEEKDAY_SLOTS,
    NFB_SATURDAY_SLOTS,
    SATURDAY_WEEKDAY,
    NFB_SLOT_DURATION_MIN,
} from "./constants";
import { PSY_SERVICE, NFB_SERVICE } from "../../utils/constants";
import store2 from "store2";

const PSY_SLOT_DURATION_MIN = 60;

const fetchReservedSlotsCalendar = async (
    startDate: Date,
    serviceId: number,
    clinicId: number | "",
    therapistId: string | ""
): Promise<CalendarResponse> => {
    const tokens = store2.get("auth");
    const accessToken = tokens?.access_token;

    const params: Record<string, string | number> = {
        startDate: format(startDate, "yyyy-MM-dd"),
        days: DAYS_WINDOW_CALENDAR_SPAN,
        serviceId,
    };

    if (clinicId !== "") {
        params.clinicId = clinicId;
    }

    if (therapistId !== "") {
        params.therapistId = therapistId;
    }

    const response = await axios({
        method: "GET",
        url: `${baseUrl}/reserved-slots-calendar`,
        params,
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });

    return response.data;
};

type FetchBothServicesResult = {
    psySlots: Array<CalendarSlot>;
    nfbSlots: Array<CalendarSlot>;
    psyAvailability: Array<AvailabilitySlot>;
    nfbAvailability: Array<AvailabilitySlot>;
    psyBookings: Array<BookingSlot>;
    nfbBookings: Array<BookingSlot>;
    nfbBlockedRules: Array<NfbBlockedRule>;
};

const fetchBothServices = async (
    startDate: Date,
    clinicId: number | "",
    therapistId: string | ""
): Promise<FetchBothServicesResult> => {
    const shouldFetchPsy = therapistId !== "";
    // NFB capacity/bookings are per-clinic — never fetch (and merge) across clinics (D3).
    const shouldFetchNfb = clinicId !== "";

    const emptyResponse: CalendarResponse = { slots: [], availability: [], bookings: [] };

    const [psyResponse, nfbResponse] = await Promise.all([
        shouldFetchPsy
            ? fetchReservedSlotsCalendar(startDate, PSY_SERVICE, clinicId, therapistId)
            : Promise.resolve(emptyResponse),
        shouldFetchNfb
            ? fetchReservedSlotsCalendar(startDate, NFB_SERVICE, clinicId, "")
            : Promise.resolve(emptyResponse),
    ]);

    const psySlotsWithService = psyResponse.slots.map((slot) => ({ ...slot, serviceId: PSY_SERVICE }));
    const nfbSlotsWithService = nfbResponse.slots.map((slot) => ({ ...slot, serviceId: NFB_SERVICE }));

    return {
        psySlots: psySlotsWithService,
        nfbSlots: nfbSlotsWithService,
        psyAvailability: psyResponse.availability ?? [],
        nfbAvailability: nfbResponse.availability ?? [],
        psyBookings: psyResponse.bookings ?? [],
        nfbBookings: nfbResponse.bookings ?? [],
        nfbBlockedRules: nfbResponse.nfbCapacity?.blocked ?? [],
    };
};

export const useReservedSlotsCalendar = ({ startDate, clinicId, therapistId, enabled }: UseReservedSlotsCalendarParams) => {
    const queryKey = [
        "reserved-slots-calendar-unified",
        format(startDate, "yyyy-MM-dd"),
        clinicId,
        therapistId,
    ];

    const { data, isLoading, isFetching, refetch } = useQuery({
        queryKey,
        queryFn: () => fetchBothServices(startDate, clinicId, therapistId),
        enabled,
        refetchOnWindowFocus: false,
    });

    const psySlots = data?.psySlots ?? [];
    const nfbSlots = data?.nfbSlots ?? [];
    const psyAvailability = data?.psyAvailability ?? [];
    const nfbAvailability = data?.nfbAvailability ?? [];
    const psyBookings = data?.psyBookings ?? [];
    const nfbBookings = data?.nfbBookings ?? [];
    const nfbBlockedRules = data?.nfbBlockedRules ?? [];
    const allSlots = [...psySlots, ...nfbSlots];

    const buildAvailabilityMap = (availability: Array<AvailabilitySlot>) => {
        const availabilityMap: Record<string, AvailabilitySlot> = {};

        availability.forEach((slot) => {
            const key = slotKey(slot.date, slot.time);
            availabilityMap[key] = slot;
        });

        return availabilityMap;
    };

    const psyAvailabilityByDateAndTime = useMemo(() => buildAvailabilityMap(psyAvailability), [psyAvailability]);
    const nfbAvailabilityByDateAndTime = useMemo(() => buildAvailabilityMap(nfbAvailability), [nfbAvailability]);

    const getAvailabilityStatus = (dateKey: string, time: string, service: "psy" | "nfb" = "psy"): "available" | "time_off" | null => {
        const key = slotKey(dateKey, time);
        const availabilityMap = service === "psy" ? psyAvailabilityByDateAndTime : nfbAvailabilityByDateAndTime;
        return availabilityMap[key]?.status ?? null;
    };

    const slotsByDateAndTime = useMemo(() => {
        const slotsMap: Record<string, SlotsByService> = {};

        const processSlots = (slots: Array<CalendarSlot>, serviceId: number) => {
            slots.forEach((slot) => {
                const key = slotKey(slot.date, slot.time);

                if (!slotsMap[key]) {
                    slotsMap[key] = { psy: [], nfb: [] };
                }

                const slotData: ReservedSlotData = {
                    clientId: slot.clientId,
                    clientNumericId: slot.clientNumericId,
                    clientName: slot.clientName,
                    therapistName: slot.therapistName,
                    treatmentId: slot.treatmentId,
                    servicesProvidedId: slot.servicesProvidedId,
                    isWeekly: slot.isWeekly,
                    serviceId,
                    missedTwoConsecutiveSessions: slot.missedTwoConsecutiveSessions,
                    hasScheduleMismatch: slot.hasScheduleMismatch,
                };

                if (serviceId === PSY_SERVICE) {
                    slotsMap[key].psy.push(slotData);
                } else {
                    slotsMap[key].nfb.push(slotData);
                }
            });
        };

        processSlots(psySlots, PSY_SERVICE);
        processSlots(nfbSlots, NFB_SERVICE);

        return slotsMap;
    }, [psySlots, nfbSlots]);

    const getSlotsForDateAndTime = (dateKey: string, time: string): SlotsByService => {
        const timeNormalized = time.substring(0, 5);
        const key = `${dateKey}_${timeNormalized}`;
        return slotsByDateAndTime[key] ?? { psy: [], nfb: [] };
    };

    const psyBookingsByDateAndTime = useMemo(
        () => buildBookingsIndex(psyBookings, () => PSY_HOURLY_SLOTS, PSY_SLOT_DURATION_MIN),
        [psyBookings]
    );
    const nfbBookingsByDateAndTime = useMemo(
        () =>
            buildBookingsIndex(
                nfbBookings,
                (weekDay) => (weekDay === SATURDAY_WEEKDAY ? NFB_SATURDAY_SLOTS : NFB_WEEKDAY_SLOTS),
                NFB_SLOT_DURATION_MIN
            ),
        [nfbBookings]
    );

    const getBookingsForDateAndTime = (dateKey: string, time: string): BookingsByService => {
        const key = slotKey(dateKey, time);
        return {
            psy: psyBookingsByDateAndTime[key] ?? [],
            nfb: nfbBookingsByDateAndTime[key] ?? [],
        };
    };

    const getNfbCapacity = (dateKey: string, time: string) => {
        const capacity = nfbAvailabilityByDateAndTime[slotKey(dateKey, time)]?.capacity ?? 0;
        return capacity;
    };

    const isNfbBlocked = (dateKey: string, time: string) => {
        const weekDayName = format(parseISO(dateKey), "EEEE").toLowerCase();
        const isBlockedByRule = nfbBlockedRules.some(
            (rule) =>
                (!rule.day || rule.day.toLowerCase() === weekDayName) &&
                (!rule.time || (time >= rule.time.start && time < rule.time.end))
        );
        const hasNoCapacity = nfbAvailabilityByDateAndTime[slotKey(dateKey, time)]?.capacity === 0;
        const isBlocked = isBlockedByRule || hasNoCapacity;
        return isBlocked;
    };

    // Binding usage for "can a reservation be placed here?". Reservations and the date's sessions are two
    // independent axes against the same device capacity, so a slot is reservable only when BOTH are below
    // capacity — i.e. max(reservations, sessions) < capacity. A one-off session never takes a reservation
    // lane, so it can only block via the device count. Consumed by the finder; the grid colours straight
    // from the two raw counts via getNfbBlockStatus.
    const getOccupancy = (dateKey: string, time: string, service: "psy" | "nfb") => {
        const reservations = getSlotsForDateAndTime(dateKey, time)[service];
        const bookings = getBookingsForDateAndTime(dateKey, time)[service];
        const bindingUsage = Math.max(reservations.length, bookings.length);
        return bindingUsage;
    };

    return {
        allSlots,
        psySlots,
        nfbSlots,
        slotsByDateAndTime,
        getSlotsForDateAndTime,
        getBookingsForDateAndTime,
        getAvailabilityStatus,
        getNfbCapacity,
        isNfbBlocked,
        getOccupancy,
        isLoading: enabled && (isLoading || isFetching),
        refetch,
    };
};
